import React, { useState, useEffect, useRef } from 'react';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { makeRequest2 } from '../../../../../utils/makeRequest';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { Chart } from 'primereact/chart';
import { TabView, TabPanel } from 'primereact/tabview';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const ProfitLoss = () => {
    const [loading, setLoading] = useState(false);
    const [startDate, setStartDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1));
    const [endDate, setEndDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 0));
    const [profitLossData, setProfitLossData] = useState(null);
    const [compareWith, setCompareWith] = useState(null);
    const [periodType, setPeriodType] = useState('month');
    const [accounts, setAccounts] = useState([]);
    const [glEntries, setGlEntries] = useState([]);
    const toast = useRef(null);

    const facilityId = "68354b430bfa0b7ac72078c5"; // Your facility ID

    const compareOptions = [
        { label: 'No Comparison', value: null },
        { label: 'Previous Period', value: 'previous' },
        { label: 'Same Period Last Year', value: 'lastYear' }
    ];

    const periodOptions = [
        { label: 'Monthly', value: 'month' },
        { label: 'Quarterly', value: 'quarter' },
        { label: 'Yearly', value: 'year' }
    ];

    useEffect(() => {
        fetchProfitLossData();
    }, [startDate, endDate, compareWith, periodType]);

    const fetchProfitLossData = async () => {
        try {
            setLoading(true);

            // Fetch accounts and GL entries
            const accountsData = await fetchAccounts();
            const entriesData = await fetchGLEntries();

            // Process the data to create profit & loss statement
            processProfitLossData(accountsData, entriesData);

        } catch (error) {
            console.error('Error fetching profit and loss data:', error);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to fetch profit and loss data: ' + (error.message || 'Unknown error')
            });
        } finally {
            setLoading(false);
        }
    };

    const fetchAccounts = async () => {
        try {
            const response = await makeRequest2(`/api/v1/gl-accounts/${facilityId}`, 'GET');

            let fetchedAccounts = [];
            if (response?.data?.data) {
                fetchedAccounts = Array.isArray(response.data.data) ? response.data.data : [response.data.data];
            } else if (response?.data) {
                fetchedAccounts = Array.isArray(response.data) ? response.data : [response.data];
            } else if (Array.isArray(response)) {
                fetchedAccounts = response;
            }

            console.log('Fetched accounts:', fetchedAccounts);
            setAccounts(fetchedAccounts);
            return fetchedAccounts;

        } catch (error) {
            console.error('Error fetching accounts:', error);
            throw error;
        }
    };

    const fetchGLEntries = async () => {
        try {
            // Build query parameters for date filtering
            const queryParams = new URLSearchParams();
            if (startDate) {
                queryParams.append('startDate', startDate.toISOString());
            }
            if (endDate) {
                queryParams.append('endDate', endDate.toISOString());
            }

            const endpoint = `/api/v1/gl-entries/get-all-entries/${facilityId}${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
            const response = await makeRequest2(endpoint, 'GET');

            let fetchedEntries = [];
            if (response?.data?.data) {
                fetchedEntries = Array.isArray(response.data.data) ? response.data.data : [response.data.data];
            } else if (response?.data) {
                fetchedEntries = Array.isArray(response.data) ? response.data : [response.data];
            } else if (Array.isArray(response)) {
                fetchedEntries = response;
            }

            console.log('Fetched GL entries:', fetchedEntries);
            setGlEntries(fetchedEntries);
            return fetchedEntries;

        } catch (error) {
            console.error('Error fetching GL entries:', error);
            throw error;
        }
    };

    const processProfitLossData = (accountsData, entriesData) => {
        if (!accountsData || !entriesData) {
            setProfitLossData(null);
            return;
        }

        // Group entries by account
        const accountEntries = {};
        entriesData.forEach(entry => {
            const { accountId, amount, entryType } = entry;
            if (!accountEntries[accountId]) {
                accountEntries[accountId] = { debit: 0, credit: 0 };
            }

            if (entryType === 'debit') {
                accountEntries[accountId].debit += amount;
            } else if (entryType === 'credit') {
                accountEntries[accountId].credit += amount;
            }
        });

        // Separate revenue and expense accounts
        const revenue = [];
        const expenses = [];

        accountsData.forEach(account => {
            const accountType = account.accountType?.toLowerCase();
            const accountId = account._id;
            const entries = accountEntries[accountId] || { debit: 0, credit: 0 };

            // Calculate net amount based on account type
            let netAmount = 0;

            if (accountType === 'revenue') {
                // Revenue accounts: credit increases revenue
                netAmount = entries.credit - entries.debit;
                if (netAmount !== 0) {
                    revenue.push({
                        code: account.accountCode,
                        name: account.accountName,
                        amount: Math.abs(netAmount),
                        accountType: accountType,
                        debit: entries.debit,
                        credit: entries.credit
                    });
                }
            } else if (accountType === 'expense') {
                // Expense accounts: debit increases expense
                netAmount = entries.debit - entries.credit;
                if (netAmount !== 0) {
                    expenses.push({
                        code: account.accountCode,
                        name: account.accountName,
                        amount: Math.abs(netAmount),
                        accountType: accountType,
                        debit: entries.debit,
                        credit: entries.credit
                    });
                }
            }
        });

        // Calculate totals
        const totalRevenue = revenue.reduce((sum, item) => sum + item.amount, 0);
        const totalExpenses = expenses.reduce((sum, item) => sum + item.amount, 0);
        const netIncome = totalRevenue - totalExpenses;
        const profitMargin = totalRevenue > 0 ? (netIncome / totalRevenue) * 100 : 0;

        // Generate comparison data if requested
        let comparisonData = null;
        if (compareWith) {
            comparisonData = generateComparisonData(revenue, expenses, totalRevenue, totalExpenses, netIncome);
        }

        // Generate chart data
        const chartData = generateChartData();

        const processedData = {
            period: {
                start: startDate.toLocaleDateString(),
                end: endDate.toLocaleDateString()
            },
            compareWith,
            periodType,
            totalRevenue,
            totalExpenses,
            netIncome,
            profitMargin,
            revenue: revenue.sort((a, b) => a.code.localeCompare(b.code)),
            expenses: expenses.sort((a, b) => a.code.localeCompare(b.code)),
            comparisonData,
            chartData
        };

        console.log('Processed Profit & Loss data:', processedData);
        setProfitLossData(processedData);

        // Show success message
        toast.current.show({
            severity: 'success',
            summary: 'Success',
            detail: `Profit & Loss statement generated with ${revenue.length} revenue accounts and ${expenses.length} expense accounts`,
            life: 3000
        });
    };

    const generateComparisonData = (revenue, expenses, totalRevenue, totalExpenses, netIncome) => {
        // Calculate comparison period dates
        let compStartDate, compEndDate;

        if (compareWith === 'previous') {
            const diffTime = endDate.getTime() - startDate.getTime();
            compEndDate = new Date(startDate.getTime() - 1);
            compStartDate = new Date(compEndDate.getTime() - diffTime);
        } else if (compareWith === 'lastYear') {
            compStartDate = new Date(startDate.getFullYear() - 1, startDate.getMonth(), startDate.getDate());
            compEndDate = new Date(endDate.getFullYear() - 1, endDate.getMonth(), endDate.getDate());
        }

        // For now, generate mock comparison data
        // In a full implementation, you would fetch data for the comparison period
        const generateComparisonItem = (item) => {
            const variance = Math.random() * 0.3 - 0.1; // -10% to +20%
            const previousAmount = Math.round(item.amount * (1 - variance));
            return {
                ...item,
                previousAmount,
                change: item.amount - previousAmount,
                changePercent: previousAmount > 0 ? ((item.amount - previousAmount) / previousAmount) * 100 : 100
            };
        };

        const comparisonRevenue = revenue.map(generateComparisonItem);
        const comparisonExpenses = expenses.map(generateComparisonItem);

        const comparisonTotalRevenue = comparisonRevenue.reduce((sum, item) => sum + item.previousAmount, 0);
        const comparisonTotalExpenses = comparisonExpenses.reduce((sum, item) => sum + item.previousAmount, 0);
        const comparisonNetIncome = comparisonTotalRevenue - comparisonTotalExpenses;

        return {
            revenue: comparisonRevenue,
            expenses: comparisonExpenses,
            totalRevenue: comparisonTotalRevenue,
            totalExpenses: comparisonTotalExpenses,
            netIncome: comparisonNetIncome,
            revenueDiff: totalRevenue - comparisonTotalRevenue,
            revenueDiffPercent: comparisonTotalRevenue > 0 ? ((totalRevenue - comparisonTotalRevenue) / comparisonTotalRevenue) * 100 : 100,
            expensesDiff: totalExpenses - comparisonTotalExpenses,
            expensesDiffPercent: comparisonTotalExpenses > 0 ? ((totalExpenses - comparisonTotalExpenses) / comparisonTotalExpenses) * 100 : 100,
            netIncomeDiff: netIncome - comparisonNetIncome,
            netIncomeDiffPercent: comparisonNetIncome !== 0 ? ((netIncome - comparisonNetIncome) / Math.abs(comparisonNetIncome)) * 100 : 100
        };
    };

    const generateChartData = () => {
        // Generate chart data based on period type
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const currentMonth = new Date().getMonth();

        const chartLabels = [];
        const revenueData = [];
        const expenseData = [];
        const profitData = [];

        // Get labels based on period type
        if (periodType === 'month') {
            // Last 6 months
            for (let i = 5; i >= 0; i--) {
                const monthIndex = (currentMonth - i + 12) % 12;
                chartLabels.push(months[monthIndex]);
            }
        } else if (periodType === 'quarter') {
            // Last 4 quarters
            for (let i = 3; i >= 0; i--) {
                chartLabels.push(`Q${4 - i}`);
            }
        } else {
            // Last 3 years
            const currentYear = new Date().getFullYear();
            for (let i = 2; i >= 0; i--) {
                chartLabels.push((currentYear - i).toString());
            }
        }

        // For demo purposes, generate random data
        // In a full implementation, you would fetch historical data
        chartLabels.forEach(() => {
            const revValue = Math.floor(Math.random() * 300000) + 100000;
            const expValue = Math.floor(Math.random() * 200000) + 80000;
            revenueData.push(revValue);
            expenseData.push(expValue);
            profitData.push(revValue - expValue);
        });

        return {
            labels: chartLabels,
            datasets: [
                {
                    label: 'Revenue',
                    data: revenueData,
                    backgroundColor: 'rgba(75, 192, 192, 0.2)',
                    borderColor: 'rgb(75, 192, 192)',
                    borderWidth: 2
                },
                {
                    label: 'Expenses',
                    data: expenseData,
                    backgroundColor: 'rgba(255, 99, 132, 0.2)',
                    borderColor: 'rgb(255, 99, 132)',
                    borderWidth: 2
                },
                {
                    label: 'Net Income',
                    data: profitData,
                    backgroundColor: 'rgba(54, 162, 235, 0.2)',
                    borderColor: 'rgb(54, 162, 235)',
                    borderWidth: 2
                }
            ]
        };
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-KE', {
            style: 'currency',
            currency: 'KES',
            minimumFractionDigits: 2
        }).format(value);
    };

    const formatPercent = (value) => {
        return new Intl.NumberFormat('en-US', {
            style: 'percent',
            minimumFractionDigits: 1,
            maximumFractionDigits: 2
        }).format(value / 100);
    };

    const amountTemplate = (rowData) => {
        return formatCurrency(rowData.amount);
    };

    const compareAmountTemplate = (rowData) => {
        if (!compareWith || !rowData.previousAmount) return null;
        return formatCurrency(rowData.previousAmount);
    };

    const changeTemplate = (rowData) => {
        if (!compareWith || rowData.change === undefined) return null;

        const changeClass = rowData.change >= 0 ? 'text-success' : 'text-danger';
        const changeIcon = rowData.change >= 0 ? 'pi pi-arrow-up' : 'pi pi-arrow-down';

        return (
            <span className={changeClass}>
                <i className={`${changeIcon} mr-1`}></i>
                {formatCurrency(Math.abs(rowData.change))} ({Math.abs(rowData.changePercent).toFixed(1)}%)
            </span>
        );
    };

    const generatePDF = () => {
        if (!profitLossData) {
            toast.current.show({
                severity: 'warn',
                summary: 'Warning',
                detail: 'No data available to export'
            });
            return;
        }

        try {
            const doc = new jsPDF();

            // Add title
            doc.setFontSize(18);
            doc.setFont('helvetica', 'bold');
            doc.text('Profit & Loss Statement', 105, 20, { align: 'center' });

            // Add date range
            doc.setFontSize(12);
            doc.setFont('helvetica', 'normal');
            const dateText = `From ${profitLossData.period.start} to ${profitLossData.period.end}`;
            doc.text(dateText, 105, 30, { align: 'center' });

            let yPosition = 50;

            // Revenue section
            doc.setFontSize(14);
            doc.setFont('helvetica', 'bold');
            doc.text('REVENUE', 20, yPosition);
            yPosition += 10;

            // Revenue table
            const revenueTableData = profitLossData.revenue.map(item => [
                item.code,
                item.name,
                formatCurrency(item.amount)
            ]);

            doc.autoTable({
                head: [['Code', 'Account Name', 'Amount']],
                body: revenueTableData,
                startY: yPosition,
                theme: 'grid',
                headStyles: {
                    fillColor: [75, 192, 192],
                    textColor: 255,
                    fontStyle: 'bold'
                },
                styles: {
                    fontSize: 10,
                    cellPadding: 3
                }
            });

            yPosition = doc.lastAutoTable.finalY + 10;

            // Total revenue
            doc.setFont('helvetica', 'bold');
            doc.text(`Total Revenue: ${formatCurrency(profitLossData.totalRevenue)}`, 20, yPosition);
            yPosition += 20;

            // Expenses section
            doc.setFontSize(14);
            doc.text('EXPENSES', 20, yPosition);
            yPosition += 10;

            // Expenses table
            const expenseTableData = profitLossData.expenses.map(item => [
                item.code,
                item.name,
                formatCurrency(item.amount)
            ]);

            doc.autoTable({
                head: [['Code', 'Account Name', 'Amount']],
                body: expenseTableData,
                startY: yPosition,
                theme: 'grid',
                headStyles: {
                    fillColor: [255, 99, 132],
                    textColor: 255,
                    fontStyle: 'bold'
                },
                styles: {
                    fontSize: 10,
                    cellPadding: 3
                }
            });

            yPosition = doc.lastAutoTable.finalY + 10;

            // Total expenses
            doc.text(`Total Expenses: ${formatCurrency(profitLossData.totalExpenses)}`, 20, yPosition);
            yPosition += 15;

            // Net income
            doc.setFontSize(16);
            const netIncomeColor = profitLossData.netIncome >= 0 ? [0, 128, 0] : [255, 0, 0];
            doc.setTextColor(...netIncomeColor);
            doc.text(`Net Income: ${formatCurrency(profitLossData.netIncome)}`, 20, yPosition);
            doc.setTextColor(0, 0, 0); // Reset to black

            // Save the PDF
            const fileName = `profit-loss-${endDate ? endDate.toISOString().split('T')[0] : 'current'}.pdf`;
            doc.save(fileName);

            toast.current.show({
                severity: 'success',
                summary: 'Success',
                detail: 'PDF exported successfully'
            });

        } catch (error) {
            console.error('Error generating PDF:', error);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to generate PDF: ' + error.message
            });
        }
    };

    const handleExport = () => {
        generatePDF();
    };

    const handlePeriodChange = (e) => {
        setPeriodType(e.value);

        // Adjust dates based on period type
        const now = new Date();
        const year = now.getFullYear();
        const month = now.getMonth();

        if (e.value === 'month') {
            // Previous month
            setStartDate(new Date(year, month - 1, 1));
            setEndDate(new Date(year, month, 0));
        } else if (e.value === 'quarter') {
            // Current quarter
            const quarter = Math.floor(month / 3);
            setStartDate(new Date(year, quarter * 3, 1));
            setEndDate(new Date(year, (quarter + 1) * 3, 0));
        } else if (e.value === 'year') {
            // Current year
            setStartDate(new Date(year, 0, 1));
            setEndDate(new Date(year, 11, 31));
        }
    };

    const renderHeader = (title) => {
        return (
            <div className="d-flex justify-content-between align-items-center">
                <h5 className="m-0">{title}</h5>
            </div>
        );
    };

    const getChartOptions = () => {
        return {
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    position: 'bottom'
                },
                tooltip: {
                    callbacks: {
                        label: function (context) {
                            let label = context.dataset.label || '';
                            if (label) {
                                label += ': ';
                            }
                            if (context.parsed.y !== null) {
                                label += formatCurrency(context.parsed.y);
                            }
                            return label;
                        }
                    }
                }
            }
        };
    };

    return (
        <div className="profit-loss-report">
            <Toast ref={toast} />

            <div className="card mb-4">
                <div className="card-body">
                    <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
                        <h4 className="mb-3 mb-md-0">Profit & Loss Statement</h4>

                        <div className="d-flex flex-wrap gap-2">
                            <div className="p-field">
                                <label htmlFor="period-type" className="font-weight-bold mr-2">Period:</label>
                                <Dropdown
                                    id="period-type"
                                    value={periodType}
                                    options={periodOptions}
                                    onChange={handlePeriodChange}
                                    className="w-auto"
                                />
                            </div>

                            <div className="p-field">
                                <label htmlFor="start-date" className="font-weight-bold mr-2">From:</label>
                                <Calendar
                                    id="start-date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.value)}
                                    showIcon
                                    className="w-auto"
                                />
                            </div>

                            <div className="p-field">
                                <label htmlFor="end-date" className="font-weight-bold mr-2">To:</label>
                                <Calendar
                                    id="end-date"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.value)}
                                    showIcon
                                    className="w-auto"
                                />
                            </div>

                            <div className="p-field">
                                <label htmlFor="compare" className="font-weight-bold mr-2">Compare:</label>
                                <Dropdown
                                    id="compare"
                                    value={compareWith}
                                    options={compareOptions}
                                    onChange={(e) => setCompareWith(e.value)}
                                    className="w-auto"
                                />
                            </div>

                            <Button
                                label="Refresh"
                                icon="pi pi-refresh"
                                onClick={fetchProfitLossData}
                                className="ml-2"
                            />

                            <Button
                                label="Export PDF"
                                icon="pi pi-file-pdf"
                                onClick={handleExport}
                                className="ml-2 p-button-success"
                            />
                        </div>
                    </div>

                    {loading ? (
                        <div className="text-center p-4">
                            <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }}></i>
                            <p className="mt-3">Loading profit & loss data...</p>
                        </div>
                    ) : profitLossData ? (
                        <div>
                            <div className="text-center mb-4">
                                <h5>Profit & Loss Statement</h5>
                                <p className="text-muted mb-0">
                                    {profitLossData.period.start} to {profitLossData.period.end}
                                </p>
                                {compareWith && (
                                    <p className="text-muted">
                                        Compared to {compareWith === 'previous' ? 'Previous Period' : 'Same Period Last Year'}
                                    </p>
                                )}
                            </div>

                            <TabView>
                                <TabPanel header="Summary">
                                    {/* Summary Cards */}
                                    <div className="row mb-4">
                                        <div className="col-md-6 col-lg-3 mb-3">
                                            <Card className="h-100 shadow-sm">
                                                <div className="text-center">
                                                    <h6 className="text-muted">Total Revenue</h6>
                                                    <h3 className="text-success">{formatCurrency(profitLossData.totalRevenue)}</h3>
                                                    {compareWith && profitLossData.comparisonData && (
                                                        <p className={profitLossData.comparisonData.revenueDiff >= 0 ? 'text-success mb-0' : 'text-danger mb-0'}>
                                                            <i className={profitLossData.comparisonData.revenueDiff >= 0 ? 'pi pi-arrow-up mr-1' : 'pi pi-arrow-down mr-1'}></i>
                                                            {formatPercent(Math.abs(profitLossData.comparisonData.revenueDiffPercent))}
                                                        </p>
                                                    )}
                                                </div>
                                            </Card>
                                        </div>
                                        <div className="col-md-6 col-lg-3 mb-3">
                                            <Card className="h-100 shadow-sm">
                                                <div className="text-center">
                                                    <h6 className="text-muted">Total Expenses</h6>
                                                    <h3 className="text-danger">{formatCurrency(profitLossData.totalExpenses)}</h3>
                                                    {compareWith && profitLossData.comparisonData && (
                                                        <p className={profitLossData.comparisonData.expensesDiff <= 0 ? 'text-success mb-0' : 'text-danger mb-0'}>
                                                            <i className={profitLossData.comparisonData.expensesDiff <= 0 ? 'pi pi-arrow-down mr-1' : 'pi pi-arrow-up mr-1'}></i>
                                                            {formatPercent(Math.abs(profitLossData.comparisonData.expensesDiffPercent))}
                                                        </p>
                                                    )}
                                                </div>
                                            </Card>
                                        </div>
                                        <div className="col-md-6 col-lg-3 mb-3">
                                            <Card className="h-100 shadow-sm">
                                                <div className="text-center">
                                                    <h6 className="text-muted">Net Income</h6>
                                                    <h3 className={profitLossData.netIncome >= 0 ? 'text-success' : 'text-danger'}>
                                                        {formatCurrency(profitLossData.netIncome)}
                                                    </h3>
                                                    {compareWith && profitLossData.comparisonData && (
                                                        <p className={profitLossData.comparisonData.netIncomeDiff >= 0 ? 'text-success mb-0' : 'text-danger mb-0'}>
                                                            <i className={profitLossData.comparisonData.netIncomeDiff >= 0 ? 'pi pi-arrow-up mr-1' : 'pi pi-arrow-down mr-1'}></i>
                                                            {formatPercent(Math.abs(profitLossData.comparisonData.netIncomeDiffPercent))}
                                                        </p>
                                                    )}
                                                </div>
                                            </Card>
                                        </div>
                                        <div className="col-md-6 col-lg-3 mb-3">
                                            <Card className="h-100 shadow-sm">
                                                <div className="text-center">
                                                    <h6 className="text-muted">Profit Margin</h6>
                                                    <h3 className={profitLossData.profitMargin >= 0 ? 'text-success' : 'text-danger'}>
                                                        {formatPercent(profitLossData.profitMargin)}
                                                    </h3>
                                                </div>
                                            </Card>
                                        </div>
                                    </div>

                                    {/* Chart */}
                                    <div className="card mb-4">
                                        <div className="card-body">
                                            <h5 className="card-title">Performance Trend</h5>
                                            <div style={{ height: '300px' }}>
                                                <Chart type="bar" data={profitLossData.chartData} options={getChartOptions()} />
                                            </div>
                                        </div>
                                    </div>
                                </TabPanel>

                                <TabPanel header="Detailed Statement">
                                    {/* Revenue Table */}
                                    <DataTable
                                        value={compareWith ? profitLossData.comparisonData.revenue : profitLossData.revenue}
                                        header={renderHeader('Revenue')}
                                        className="mb-4"
                                        emptyMessage="No revenue accounts found"
                                        sortField="code"
                                        sortOrder={1}
                                    >
                                        <Column field="code" header="Code" style={{ width: '10%' }} sortable />
                                        <Column field="name" header="Account" style={{ width: '40%' }} sortable />
                                        <Column field="amount" header="Amount" body={amountTemplate} style={{ width: '20%' }} sortable />
                                        {compareWith && (
                                            <>
                                                <Column field="previousAmount" header="Previous" body={compareAmountTemplate} style={{ width: '15%' }} />
                                                <Column field="change" header="Change" body={changeTemplate} style={{ width: '15%' }} />
                                            </>
                                        )}
                                    </DataTable>
                                    <div className="text-end mb-4">
                                        <strong>Total Revenue: {formatCurrency(profitLossData.totalRevenue)}</strong>
                                    </div>

                                    {/* Expenses Table */}
                                    <DataTable
                                        value={compareWith ? profitLossData.comparisonData.expenses : profitLossData.expenses}
                                        header={renderHeader('Expenses')}
                                        className="mb-4"
                                        emptyMessage="No expense accounts found"
                                        sortField="code"
                                        sortOrder={1}
                                    >
                                        <Column field="code" header="Code" style={{ width: '10%' }} sortable />
                                        <Column field="name" header="Account" style={{ width: '40%' }} sortable />
                                        <Column field="amount" header="Amount" body={amountTemplate} style={{ width: '20%' }} sortable />
                                        {compareWith && (
                                            <>
                                                <Column field="previousAmount" header="Previous" body={compareAmountTemplate} style={{ width: '15%' }} />
                                                <Column field="change" header="Change" body={changeTemplate} style={{ width: '15%' }} />
                                            </>
                                        )}
                                    </DataTable>
                                    <div className="text-end mb-4">
                                        <strong>Total Expenses: {formatCurrency(profitLossData.totalExpenses)}</strong>
                                    </div>

                                    {/* Net Income Summary */}
                                    <div className="card bg-light">
                                        <div className="card-body">
                                            <div className="row">
                                                <div className="col-md-8 offset-md-2">
                                                    <table className="table table-borderless">
                                                        <tbody>
                                                            <tr>
                                                                <td className="text-end"><strong>Total Revenue:</strong></td>
                                                                <td className="text-end">{formatCurrency(profitLossData.totalRevenue)}</td>
                                                            </tr>
                                                            <tr>
                                                                <td className="text-end"><strong>Total Expenses:</strong></td>
                                                                <td className="text-end">{formatCurrency(profitLossData.totalExpenses)}</td>
                                                            </tr>
                                                            <tr>
                                                                <td className="text-end border-top"><strong>Net Income:</strong></td>
                                                                <td className={`text-end border-top ${profitLossData.netIncome >= 0 ? 'text-success' : 'text-danger'}`}>
                                                                    <strong>{formatCurrency(profitLossData.netIncome)}</strong>
                                                                </td>
                                                            </tr>
                                                            <tr>
                                                                <td className="text-end"><strong>Profit Margin:</strong></td>
                                                                <td className={`text-end ${profitLossData.profitMargin >= 0 ? 'text-success' : 'text-danger'}`}>
                                                                    <strong>{formatPercent(profitLossData.profitMargin)}</strong>
                                                                </td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </TabPanel>
                            </TabView>
                        </div>
                    ) : (
                        <div className="text-center p-4">
                            <p>No profit & loss data available</p>
                            <p className="text-muted">Please ensure you have revenue and expense accounts with GL entries for the selected period.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ProfitLoss;